import {FormControl, PatternValidator, UntypedFormGroup, ValidatorFn, Validators} from "@angular/forms";

/**
 * Configuration for a single form field.
 * Has to be extended for each field type.
 */
export abstract class FormModel {
  /**
   * id/name of the field
   */
  public id: string;

  /**
   * type of the field, overwritten by child classes. Used i.e. in the template.
   */
  public type: string;

  /**
   * Holds the original field type for toggeling with another
   */
  public typeOriginal: string;

  /**
   * overwrite the field type to toggle between two types
   * @private
   */
  private typeOverwrite: string;

  /**
   * additional classes for the fiedl
   */
  public classes: string;

  /**
   * configuration for the validation.
   * @protected
   */
  protected validators: ValidatorFn[] = [];

  /**
   * name of the unit that should be shown on the field,
   * @protected
   */
  protected unit: string;

  /**
   * name of the unit that should be shown on the field,
   * @protected
   */
  protected secondUnit: string;

  /**
   * Should this field marked as presetted
   * @protected
   */
  protected presetMark: boolean = false;

  /**
   * Saves an extra infoimage for the info overlay
   * @protected
   */
  protected extraInfoImage: {path: string, marker: {top: number, left: number, width: number, height: number}[]} = null;

  /**
   * initial value of the field.
   * @protected
   */
  protected value: any;

  /**
   * List of options. Only for fields with type select/radio
   * @protected
   */
  protected options: string[];

  /**
   * List of disabled options. Only for fields with type select/radio
   * @protected
   */
  protected optionsDisabled: string[] = [];

  /**
   * toggle for an info/help field
   * @protected
   */
  protected info: boolean;

  /**
   * toggle for showing/hiding a teaser text between label and input
   * @protected
   */
  public hasTeaser: boolean = false;

  /**
   * Custom config for a formModel. Can be used by child classes.
   */
  public fieldData: {
    ids?: string[],
    fields?: FormModel[],
    values?: any[],
    secondId?: string,
    selectId?: string,
    fieldType?: string,
    iconPath?: string,
    readonly?: boolean,
    cleanvalue?: boolean,
    min?: number,
    max?: number,
    step?: number,
  } = {};

  /**
   * is the field active (visible) or not
   * @protected
   */
  protected active: boolean = true;

  /**
   * should this field have a toggle to deactivate it?
   */
  public hasToggle: boolean = false;

  /**
   * postfix for the id
   */
  public idPostfix: string = null;

  /**
   * use another id/string for the error message
   */
  public errorId: string = null;

  /**
   * constructor
   *
   * @param id Unique ID of the field.
   * @param validators List of validator functions to validate the value.
   * @param unit Textkey for the unit if there is one.
   * @param value Initial value of the field.
   * @param info Toggle for showing an infotext for this field.
   * @protected
   */
  protected constructor(id: string, validators: ValidatorFn[] = [], unit: string = null, value: any = null, info: boolean = true) {
    this.id = id;
    this.validators = validators;
    this.unit = unit;
    this.value = value;
    this.info = info;
  }

  /**
   * get a clone of the current object
   */
  public clone(): FormModel {
    // @ts-ignore
    return new this.constructor(this.id, this.validators, this.unit, this.value, this.info);
  }

  /**
   * Returns the ids of all fields in this object.
   */
  getIds(): string[] {
    return [this.id];
  }

  /**
   * Sets a postfix to the id
   * @param idPostfix
   */
  setIdPostfix(idPostfix: string): FormModel {
    this.idPostfix = idPostfix;

    // cahnge all ids in the object.
    this.id += idPostfix;
    if(this.fieldData.ids) {
      this.fieldData.ids = this.fieldData.ids.map(id => id + idPostfix);
    }
    if(this.fieldData.secondId) {
      this.fieldData.secondId += idPostfix;
    }
    return this;
  }

  /**
   * set/change the value of the field.
   * @param value
   */
  setValue(value: any): void {
    this.value = value;
  }

  /**
   * returns the current value of the field
   */
  getValue(): any {
    return this.value;
  }

  /**
   * returns the current value of the field
   */
  getFormattedValueFromForm(form: UntypedFormGroup, id: string = null): any {
    if(id === null) {
      id = this.getIds()[0];
    }

    if(form && form.controls[id]) {
      return form.controls[id].value;
    }
    return null;
  }

  /**
   * returns the list of options (i.e. radio/select)
   */
  getOptions(): string[] {
    return this.options;
  }

  /**
   * set/updates the options of a field
   * @param options
   */
  setOptions(options: string[]): void {
    this.options = options;
  }

  /**
   * returns the list of options (i.e. radio/select)
   */
  getOptionsDisabled(): string[] {
    return this.optionsDisabled;
  }

  /**
   * set/updates the options of a field
   * @param options
   */
  setOptionsDisabled(options: string[]): FormModel {
    this.optionsDisabled = options;
    return this;
  }

  /**
   * Is the option disabled
   */
  isOptionDisabled(option: string): boolean {
    return this.optionsDisabled.indexOf(option) !== -1;
  }

  /**
   * Is the field required
   */
  isRequired(): boolean {
    return this.validators.indexOf(Validators.required) !== -1;
  }

  /**
   * return all validators
   */
  getValidators(): ValidatorFn[] {
    // add pattern validator to handle german number formats
    if (this.fieldData.fieldType === "number" && !this.hasPatternValidator()) {
      this.validators.push(Validators.pattern(/^\d+(,\d+)?$/));
    }

    return this.validators;
  }

  /**
   * check if we have a pattern validor already to avoind double usage of pattern validator
   * @private
   */
  private hasPatternValidator(): boolean {
    return this.validators.some(validator => {
      const testControl = new FormControl('');
      const validationResult = validator(testControl);

      return validationResult && validationResult.hasOwnProperty('pattern');
    });
  }

  /**
   * sets a different/new set of validators. All existiing validators are replaced
   * @param validators
   */
  setValidators(validators: ValidatorFn[]): void {
    this.validators = validators;
  }

  /**
   * has the field an infotext
   */
  hasInfotext(): boolean {
    return this.info;
  }

  /**
   * returns the unit value
   */
  getUnit(): string {
    return this.unit;
  }

  /**
   * returns the second unit value for a double textselect
   */
  getSecondUnit(): string {
    return this.secondUnit;
  }

  /**
   * Is the field active or not
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Set field inactive (invisible)
   */
  setInactive(): FormModel {
    this.active = false;
    return this;
  }

  /**
   * Set the field active (visible)
   */
  setActive(): FormModel {
    this.active = true;
    return this;
  }

  /**
   * activate the toggle for showing/hiding a field.
   */
  activateToggle(): FormModel {
    this.hasToggle = true;
    return this;
  }

  /**
   * Overwrite the current field type with another one.
   * @param newType
   */
  overwriteType(newType: string): void {
    if(typeof this.typeOriginal === "undefined") {
      this.typeOriginal = this.type;
      this.typeOverwrite = newType;
      this.type = newType;
    }
  }

  /**
   * toggle for change the type of overwritten field.
   */
  toggleOverwrittenType(): void {
    this.type = this.type === this.typeOverwrite? this.typeOriginal : this.typeOverwrite;
  }

  /**
   * set the css classes of the field
   * @param classes
   */
  setClasses(classes: string): FormModel {
    this.classes = classes;
    return this;
  }

  /**
   * returns the list of classes for this element.
   */
  getClasses(): string {
    return this.classes;
  }

  /**
   * Set the teaser toggle to a given value.
   * @param hasTeaser
   */
  setTeaser(hasTeaser: boolean): FormModel {
    this.hasTeaser = hasTeaser;
    return this;
  }

  /**
   * sets a error id that is used for error messages
   * @param errorId
   */
  setErrorId(errorId: string): FormModel {
    this.errorId = errorId;
    return this;
  }

  /**
   * Set the readonly status of the field.
   * @param readOnly
   */
  setReadonly(readOnly: boolean): FormModel {
    this.fieldData.readonly = readOnly;
    return this;
  }

  /**
   * sets the toggle for the presetmark
   * @param presetMark
   */
  setPresetMark(presetMark: boolean): FormModel {
    this.presetMark = presetMark;
    return this;
  }

  /**
   * Return the toggle for the preset mark
   */
  getPresetMark(): boolean {
    return this.presetMark;
  }

  /**
   * get an extra image for the overlay, if set.
   */
  getExtraInfoImage(): { path: string; marker: { top: number; left: number; width: number; height: number }[] } {
    return this.extraInfoImage;
  }

  /**
   * sets an extra image for the overlay
   * @param value
   */
  setExtraInfoImage(value: { path: string; marker: { top: number; left: number; width: number; height: number }[] }): FormModel {
    this.extraInfoImage = value;
    return this;
  }

  /**
   * updates/set the fieldtype of this field.
   *
   * @param fieldType
   */
  setFieldType(fieldType: string): FormModel {
    this.fieldData.fieldType = fieldType;
    return this;
  }

  /**
   * De/activate the clean value function on input elements
   * @param value
   */
  setCleanValue(value: boolean): FormModel {
    this.fieldData.cleanvalue = value;

    return this;
  }
}
